import { useEffect, useState, type ReactNode } from 'react';
import { Menu, Search, X } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import { NotificationsBell } from './NotificationsBell';
import {
  LayoutDashboard,
  Building2,
  Store,
  Package as PackageIcon,
  CreditCard,
  Users as UsersIcon,
  Banknote,
  BookOpen,
  Wrench,
  Globe,
  Webhook,
  ClipboardList,
  Activity,
  Settings,
  Tag,
  Ticket,
  Layers,
  Megaphone,
  FileText,
  LogOut,
} from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { Logo } from '../components/Logo';
import { ThemeToggle } from './ThemeToggle';
import { PostLoginModalBanner } from './PostLoginModalBanner';
import { TopPageBanner } from './TopPageBanner';
import { cn } from '../lib/cn';
import { usePendingApplicationsCount } from '../api/admin';
import { useSession } from '../stores/session';

/**
 * Platform admin shell. Distinct visual identity: solid "Possignin Admin" wordmark,
 * no business-switcher (admins are platform-scoped), warning-tinged accent on the
 * sidebar to remind users they're holding a powerful key.
 */
export function AdminShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setDrawerOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [drawerOpen]);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <TopPageBanner />
      <AdminTopBar onOpenMobileNav={() => setDrawerOpen(true)} />

      <div className="flex">
        <aside className="hidden lg:block sticky top-14 self-start h-[calc(100vh-3.5rem)]">
          <AdminSidebar />
        </aside>

        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-zinc-950/40 backdrop-blur-[2px]"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <div className="relative w-72 h-full bg-zinc-50 dark:bg-zinc-900 shadow-lg flex">
              <AdminSidebar className="border-r-0" />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-800 transition"
                aria-label="Close navigation"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 pb-20 lg:pb-0">{children}</main>
      </div>

      <AdminBottomNav />
      <PostLoginModalBanner />
    </div>
  );
}

/* ─────────────────────────────────────────────────── */

function AdminTopBar({ onOpenMobileNav }: { onOpenMobileNav?: () => void }) {
  return (
    <header className="sticky top-0 z-40 h-14 border-b border-zinc-200 bg-white/80 backdrop-blur-md dark:bg-zinc-950/80 dark:border-zinc-700">
      <div className="h-full px-4 sm:px-5 flex items-center gap-3">
        {onOpenMobileNav && (
          <button
            type="button"
            onClick={onOpenMobileNav}
            className="lg:hidden -ml-1 w-9 h-9 grid place-items-center rounded-md text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        <Link to="/admin" className="flex items-center gap-2 -ml-1 px-1.5 py-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
          <Logo />
          <span className="font-semibold text-zinc-950 dark:text-zinc-50 tracking-tight">
            Possignin
            <span className="ml-1.5 px-1.5 py-px rounded text-[10px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 align-middle">
              ADMIN
            </span>
          </span>
        </Link>

        <div className="hidden md:flex flex-1 max-w-md mx-auto">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
            aria-label="Open command palette"
            className="w-full inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-700 transition"
          >
            <Search className="w-4 h-4" />
            <span>Search resellers, businesses, users…</span>
            <kbd className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-medium bg-white dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700">⌘K</kbd>
          </button>
        </div>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          <ThemeToggle />
          <NotificationsBell />
          <SessionAvatar />
        </div>
      </div>
    </header>
  );
}

/** Signed-in user's avatar + explicit sign-out affordance — never a hardcoded demo name. */
function SessionAvatar() {
  const user = useSession((s) => s.user);
  const display = user?.name?.trim() || user?.email?.split('@')[0] || 'Account';
  return (
    <Link
      to="/logout"
      className="ml-1 inline-flex items-center gap-2 pl-1 pr-1 sm:pr-3 py-1 rounded-full border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
      aria-label={`Sign out ${display}`}
      title={`Sign out (${display})`}
    >
      <Avatar name={display} size="sm" />
      <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-300">
        <LogOut className="w-3.5 h-3.5" />
        Sign out
      </span>
    </Link>
  );
}

function AdminSidebar({ className }: { className?: string }) {
  const { data: pendingApplicationsCount = 0 } = usePendingApplicationsCount();
  return (
    <nav
      className={cn(
        'w-60 h-full bg-zinc-50 border-r border-zinc-200 dark:bg-zinc-900 dark:border-zinc-700 flex flex-col',
        className,
      )}
      aria-label="Admin navigation"
    >
      <div className="flex-1 overflow-y-auto nice-scroll px-2.5 py-3 space-y-0.5">
        <Item to="/admin" icon={<LayoutDashboard className="w-4 h-4" />} label="Overview" exact />
        <Item
          to="/admin/resellers"
          icon={<Building2 className="w-4 h-4" />}
          label="Resellers"
          badge={pendingApplicationsCount > 0 ? pendingApplicationsCount : undefined}
        />
        <Item to="/admin/businesses" icon={<Store className="w-4 h-4" />} label="Businesses" />
        <Item to="/admin/users" icon={<UsersIcon className="w-4 h-4" />} label="Users" />

        <Section>Catalog</Section>
        <Item to="/admin/package-categories" icon={<Layers className="w-4 h-4" />} label="Categories" />
        <Item to="/admin/packages" icon={<PackageIcon className="w-4 h-4" />} label="Packages" />
        <Item to="/admin/pricing-tiers" icon={<Tag className="w-4 h-4" />} label="Pricing tiers" />
        <Item to="/admin/addons" icon={<Tag className="w-4 h-4" />} label="Add-ons" />
        <Item to="/admin/coupons" icon={<Ticket className="w-4 h-4" />} label="Coupons" />

        <Section>Money</Section>
        <Item to="/admin/payments" icon={<CreditCard className="w-4 h-4" />} label="Payments" />
        <Item to="/admin/cash-entry" icon={<Banknote className="w-4 h-4" />} label="Cash entry" />
        <Item to="/admin/gateways" icon={<Wrench className="w-4 h-4" />} label="Gateways" />
        <Item to="/admin/billing-flow" icon={<BookOpen className="w-4 h-4" />} label="Billing flow" />

        <Section>Operations</Section>
        <Item to="/admin/broadcasts" icon={<Megaphone className="w-4 h-4" />} label="Broadcasts" />
        <Item to="/admin/cms" icon={<FileText className="w-4 h-4" />} label="Marketing" />
        <Item to="/admin/domains" icon={<Globe className="w-4 h-4" />} label="Domains" />
        <Item to="/admin/webhooks" icon={<Webhook className="w-4 h-4" />} label="Webhooks" />
        <Item to="/admin/audit" icon={<ClipboardList className="w-4 h-4" />} label="Audit log" />
        <Item to="/admin/health" icon={<Activity className="w-4 h-4" />} label="System health" />
      </div>

      <div className="border-t border-zinc-200 dark:border-zinc-700 p-2.5 space-y-0.5">
        <Item to="/admin/settings" icon={<Settings className="w-4 h-4" />} label="Settings" />
        <Item to="/logout" icon={<LogOut className="w-4 h-4" />} label="Sign out" muted />
      </div>
    </nav>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 mt-5 mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
      {children}
    </div>
  );
}

function Item({
  to,
  icon,
  label,
  exact = false,
  muted = false,
  badge,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  exact?: boolean;
  muted?: boolean;
  /** Trailing red pill (e.g. count of items needing attention). */
  badge?: number;
}) {
  const location = useLocation();
  const path = location.pathname;
  const active = exact ? path === to : path === to || path.startsWith(`${to}/`);

  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition',
        active
          ? 'bg-white dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50 shadow-xs font-medium'
          : muted
          ? 'text-zinc-500 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800/70 hover:text-zinc-950 dark:hover:text-zinc-50'
          : 'text-zinc-700 dark:text-zinc-200 hover:bg-white dark:hover:bg-zinc-800/70 hover:text-zinc-950 dark:hover:text-zinc-50',
      )}
    >
      <span className={cn('flex-shrink-0', active ? 'text-brand dark:text-brand-dm' : 'text-zinc-500 dark:text-zinc-400')}>
        {icon}
      </span>
      <span className="flex-1 min-w-0 truncate">{label}</span>
      {badge !== undefined && (
        <span
          className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold bg-rose-600 text-white tabular-nums"
          aria-label={`${badge} items need attention`}
        >
          {badge}
        </span>
      )}
    </Link>
  );
}

function AdminBottomNav() {
  const location = useLocation();
  const path = location.pathname;
  const items: Array<{ to: string; icon: ReactNode; label: string; match: (p: string) => boolean }> = [
    { to: '/admin', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Home', match: (p) => p === '/admin' },
    { to: '/admin/resellers', icon: <Building2 className="w-5 h-5" />, label: 'Resellers', match: (p) => p.startsWith('/admin/resellers') },
    { to: '/admin/businesses', icon: <Store className="w-5 h-5" />, label: 'Businesses', match: (p) => p.startsWith('/admin/businesses') },
    { to: '/admin/packages', icon: <PackageIcon className="w-5 h-5" />, label: 'Packages', match: (p) => p.startsWith('/admin/packages') || p.startsWith('/admin/addons') },
    { to: '/admin/audit', icon: <ClipboardList className="w-5 h-5" />, label: 'Audit', match: (p) => p.startsWith('/admin/audit') },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-700 pb-safe"
      aria-label="Bottom navigation"
    >
      <div className="grid grid-cols-5 h-16">
        {items.map((it) => {
          const active = it.match(path);
          return (
            <Link
              key={it.to}
              to={it.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5 transition',
                active
                  ? 'text-brand dark:text-brand-dm'
                  : 'text-zinc-500 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50',
              )}
            >
              {it.icon}
              <span className="text-[11px] font-medium">{it.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
